import { useEffect, useRef } from 'react';
import { paletteFor } from '../../lib/briefModel.js';
import { BRIEF_SIDEBAR_SCROLL } from './briefWebStyles.js';

function SidebarArticleButton({ article, palette, isActive, onSelect }) {
  return (
    <button
      type="button"
      data-article-id={article.articleId || undefined}
      onClick={() => article.articleId && onSelect(article.articleId)}
      disabled={!article.articleId}
      aria-current={isActive ? 'true' : undefined}
      className={[
        'w-full text-left flex gap-2.5 py-2 pl-2.5 pr-2 rounded-sm border-l-[3px] transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-hxd-orange focus-visible:ring-offset-1',
        isActive ? '' : 'border-l-transparent hover:bg-[#FAFAFA]',
        !article.articleId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      ].join(' ')}
      style={
        isActive
          ? { backgroundColor: palette.track, borderLeftColor: palette.bar }
          : undefined
      }
    >
      <span
        className="font-mono text-[11px] font-bold shrink-0 pt-0.5 tabular-nums"
        style={{ color: palette.bar }}
      >
        {article.number}
      </span>
      <span className="font-sans text-[13px] leading-snug text-[#222] font-semibold line-clamp-3 min-w-0">
        {article.title}
      </span>
    </button>
  );
}

function SidebarSection({ section, activeArticleId, onArticleSelect }) {
  const palette = paletteFor(section.category);

  return (
    <details open className="group border-b border-[#E8E8E8] last:border-b-0">
      <summary className="flex items-center gap-2 cursor-pointer list-none py-2.5 px-1 [&::-webkit-details-marker]:hidden">
        <span
          className="w-1 h-5 shrink-0 rounded-sm"
          style={{ backgroundColor: palette.bar }}
          aria-hidden="true"
        />
        <span className="font-sans text-[11px] font-bold tracking-[0.14em] uppercase text-black flex-1 min-w-0">
          {section.category}
        </span>
        <span className="font-mono text-[11px] font-bold" style={{ color: palette.bar }}>
          {section.articles.length}
        </span>
        <span
          className="text-[#737373] text-[10px] font-bold uppercase tracking-wide group-open:rotate-180 transition-transform"
          aria-hidden="true"
        >
          ▾
        </span>
      </summary>
      <div className="pb-2 space-y-0.5">
        {section.articles.map((article) => (
          <SidebarArticleButton
            key={article.articleId || article.number}
            article={article}
            palette={palette}
            isActive={Boolean(article.articleId && activeArticleId === article.articleId)}
            onSelect={onArticleSelect}
          />
        ))}
      </div>
    </details>
  );
}

/**
 * @param {'desktop' | 'mobile'} [variant]
 */
export default function BriefArticleSidebar({
  model,
  activeArticleId,
  onArticleSelect,
  variant = 'desktop',
}) {
  const totalArticles = model.articles?.length || 0;
  const isDesktop = variant === 'desktop';
  const listRef = useRef(null);

  // Keep the scroll-spy highlight visible inside the sidebar's own scroll area.
  useEffect(() => {
    const list = listRef.current;
    if (!isDesktop || !activeArticleId || !list) return;

    const activeEl = list.querySelector(
      `[data-article-id="${CSS.escape(activeArticleId)}"]`
    );
    if (!activeEl) return;

    const pad = 6;
    const listRect = list.getBoundingClientRect();
    const elRect = activeEl.getBoundingClientRect();
    const inView =
      elRect.top >= listRect.top + pad && elRect.bottom <= listRect.bottom - pad;

    if (!inView) {
      activeEl.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }, [activeArticleId, isDesktop]);

  if (totalArticles === 0) return null;

  const shellClass = isDesktop
    ? 'hidden lg:block sticky top-[7.5rem] self-start max-h-[calc(100dvh-8.5rem)] min-h-0'
    : 'lg:hidden mb-8';

  const panelClass = isDesktop
    ? 'border border-black bg-white flex flex-col max-h-[calc(100dvh-8.5rem)] min-h-0'
    : 'border border-black bg-white';

  const listClass = isDesktop
    ? `px-1 py-1 flex-1 min-h-0 overflow-y-auto overscroll-y-contain ${BRIEF_SIDEBAR_SCROLL}`
    : 'px-1 py-1';

  return (
    <aside className={shellClass} aria-label="Articles in this brief">
      <div className={panelClass}>
        <div className="shrink-0 px-4 py-3 border-b border-black bg-[#FAFAFA]">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#737373] font-bold">
            In this brief
          </div>
          <div className="font-sans text-[13px] font-bold text-black mt-1">
            {totalArticles} {totalArticles === 1 ? 'article' : 'articles'}
          </div>
        </div>

        <div className="shrink-0 px-2 py-2 border-b border-[#E8E8E8]">
          <button
            type="button"
            onClick={() => onArticleSelect?.('__summary__')}
            className="w-full text-left font-sans text-[12px] font-bold uppercase tracking-[0.12em] text-[#525252] hover:text-black py-1.5 px-2 rounded-sm hover:bg-[#FAFAFA] transition-colors"
          >
            ↑ Daily summary
          </button>
        </div>

        <div ref={isDesktop ? listRef : undefined} className={listClass}>
          {model.sections.map((section) => (
            <SidebarSection
              key={section.category}
              section={section}
              activeArticleId={activeArticleId}
              onArticleSelect={onArticleSelect}
            />
          ))}
        </div>

        {isDesktop && (
          <div className="shrink-0 px-4 py-3 border-t border-[#E8E8E8] bg-white">
            <p className="font-mono text-[10px] text-[#737373] leading-relaxed m-0">
              Tip: URL updates as you jump — share a specific story with{' '}
              <span className="text-black">#article-…</span>
            </p>
          </div>
        )}
      </div>
    </aside>
  );
}
